import { useState, forwardRef } from "react";
import { FormControl, FormLabel, Box, Flex, FlexProps } from "@chakra-ui/react";
import { motion } from "framer-motion";
import KYDSelect from "./KYDSelect";
import { SelectProps } from "@chakra-ui/react";

type KYDSelectProps = SelectProps & {
  options: { label: string; value: string }[];
  placeholder?: string;
};

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionLabel = motion(FormLabel);

interface KYDLabeledSelectProps extends Omit<KYDSelectProps, "label"> {
  label: string;
  containerStyle?: FlexProps;
  error?: string;
}

const KYDLabeledSelect = forwardRef<HTMLSelectElement, KYDLabeledSelectProps>(
  (
    { label, value, isDisabled = false, containerStyle, error, ...props },
    ref
  ) => {
    const [focus, setFocus] = useState(false);
    const hasValue = value !== undefined && value !== null && value !== "";
    const active = focus || hasValue;

    return (
      <FormControl isInvalid={!!error} w="100%">
        <MotionBox
          h="50px"
          boxShadow="0px 0px 10px 0px #0000000D"
          border="1px solid #E6E6E6"
          borderRadius="8px"
          p={3}
          py={active ? 1 : 3}
          transition={{ duration: 0.2, ease: "easeOut" }}
          animate={{
            paddingTop: active ? 4 : 12,
            paddingBottom: active ? 4 : 12,
          }}
          {...containerStyle}
        >
          <MotionFlex
            direction={active ? "column" : "row"}
            align={active ? "stretch" : "center"}
            gap={active ? 0 : 2}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {label && (
              <MotionLabel
                htmlFor={props.name}
                fontSize={active ? "12px" : "14px"}
                fontWeight={active ? "400" : "500"}
                color="#00000080"
                whiteSpace={active ? "normal" : "nowrap"}
                mb={0}
                mr={1}
                pointerEvents="none"
                display="flex"
                alignItems="center"
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                {label}
              </MotionLabel>
            )}

            <Box
              flex="1"
              display="flex"
              alignItems="stretch"
              w="100%"
              h="100%"
              position="relative"
            >
              <KYDSelect
                ref={ref}
                value={value}
                isDisabled={isDisabled}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                variant="unstyled"
                border="none"
                boxShadow="none"
                padding={0}
                paddingInlineStart={0}
                paddingInlineEnd={0}
                paddingRight="20px"
                height="100%"
                css={{
                  paddingInlineStart: "0 !important",
                  paddingInlineEnd: "20px !important",
                  height: "100%",
                  "::-ms-value": {
                    paddingInlineStart: "0 !important",
                  },
                  "& > option": {
                    paddingRight: "0",
                  },
                  appearance: "none",
                  "& .chakra-select__icon-wrapper": {
                    right: "0 !important",
                  },
                }}
                {...props}
              />
            </Box>
          </MotionFlex>
        </MotionBox>
      </FormControl>
    );
  }
);

KYDLabeledSelect.displayName = "KYDLabeledSelect";

export default KYDLabeledSelect;
